import { Settings, FileText, ArrowRight } from 'lucide-react';
import { Button } from '@components/base/actions';
import { Card, CardHeader, CardContent, CardFooter, VStack, HStack } from '@components/base/layout';
import { Icon, Text } from '@components/base/display';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

const variants = ['default', 'outlined', 'elevated'] as const;
const paddings = ['none', 'sm', 'md', 'lg'] as const;

function CardShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="card">
          <Card
            variant={playgroundProps.variant as 'default' | 'outlined' | 'elevated'}
            padding={playgroundProps.padding as 'none' | 'sm' | 'md' | 'lg'}
            className="max-w-md"
          >
            <CardContent>
              <Text size="lg" weight="semibold">Policy Overview</Text>
              <Text size="sm" color="muted" className="mt-2">
                Review your current coverage and policy details.
              </Text>
            </CardContent>
          </Card>
      </ShowcasePreviewSection>

      {/* Variants */}
      <ShowcaseDemoSection
        title="Variants"
        description="Cards come in three visual styles: default (shadow + border), outlined (border only), and elevated (shadow only)"
       
      >
          <HStack gap={4} wrap="wrap" align="start">
            {variants.map((variant) => (
              <Card key={variant} variant={variant} className="w-64">
                <CardContent>
                  <Text size="sm" weight="semibold" className="capitalize">
                    {variant}
                  </Text>
                  <Text size="xs" color="muted" className="mt-1">
                    {variant === 'default' && 'Shadow and border'}
                    {variant === 'outlined' && 'Border only, no shadow'}
                    {variant === 'elevated' && 'Shadow only, no border'}
                  </Text>
                </CardContent>
              </Card>
            ))}
          </HStack>
      </ShowcaseDemoSection>

      {/* Padding Options */}
      <ShowcaseDemoSection
        title="Padding Options"
        description="Control the internal spacing with padding prop: none, sm (12px), md (20px), lg (28px)"
       
      >
          <HStack gap={4} wrap="wrap" align="start">
            {paddings.map((padding) => (
              <Card key={padding} padding={padding} className="w-48">
                <CardContent>
                  <Text size="sm" weight="semibold" className="capitalize">
                    {padding}
                  </Text>
                  <Text size="xs" color="muted">padding</Text>
                </CardContent>
              </Card>
            ))}
          </HStack>
      </ShowcaseDemoSection>

      {/* With Header/Content/Footer */}
      <ShowcaseDemoSection
        title="With Header, Content, and Footer"
        description="Cards support a structured layout with header, content, and footer sections"
       
      >
          <VStack gap={4}>
            <Card className="max-w-md">
              <CardHeader title="Claim Details" subtitle="Submitted on Feb 5, 2026" />
              <CardContent>
                <VStack gap={2}>
                  <HStack justify="between">
                    <Text size="sm" color="muted">Claim ID</Text>
                    <Text size="sm">CLM-2026-001234</Text>
                  </HStack>
                  <HStack justify="between">
                    <Text size="sm" color="muted">Type</Text>
                    <Text size="sm">Auto Collision</Text>
                  </HStack>
                  <HStack justify="between">
                    <Text size="sm" color="muted">Status</Text>
                    <Text size="sm" color="secondary">Under Review</Text>
                  </HStack>
                </VStack>
              </CardContent>
              <CardFooter>
                <HStack justify="end" gap={2}>
                  <Button variant="ghost" size="sm">Cancel</Button>
                  <Button variant="primary" size="sm">View Details</Button>
                </HStack>
              </CardFooter>
            </Card>

            <Card className="max-w-md">
              <CardHeader
                title="Quick Actions"
                action={<Button variant="ghost" size="sm" leftIcon={<Icon icon={Settings} size="sm" />} aria-label="Settings" />}
              />
              <CardContent>
                <Text size="sm" color="muted">
                  Common tasks and shortcuts for managing your policies.
                </Text>
              </CardContent>
            </Card>
          </VStack>
      </ShowcaseDemoSection>

      {/* Collapsible Cards */}
      <ShowcaseDemoSection
        title="Collapsible Cards"
        description="Cards can be made collapsible by adding the collapsible prop to the header"
       
      >
          <VStack gap={4} className="max-w-md">
            <Card>
              <CardHeader
                title="Policy Documents"
                subtitle="3 documents available"
                collapsible
                defaultExpanded
              />
              <CardContent>
                <VStack gap={2}>
                  <HStack gap={2} align="center">
                    <Icon icon={FileText} size="sm" />
                    <Text size="sm">Policy Declaration</Text>
                  </HStack>
                  <HStack gap={2} align="center">
                    <Icon icon={FileText} size="sm" />
                    <Text size="sm">Coverage Summary</Text>
                  </HStack>
                  <HStack gap={2} align="center">
                    <Icon icon={FileText} size="sm" />
                    <Text size="sm">Terms and Conditions</Text>
                  </HStack>
                </VStack>
              </CardContent>
            </Card>

            <Card>
              <CardHeader
                title="Additional Coverage"
                subtitle="Optional add-ons"
                collapsible
                defaultExpanded={false}
              />
              <CardContent>
                <Text size="sm" color="muted">
                  Explore additional coverage options to enhance your policy protection.
                </Text>
              </CardContent>
            </Card>
          </VStack>
      </ShowcaseDemoSection>

      {/* Clickable Cards */}
      <ShowcaseDemoSection
        title="Clickable Cards"
        description="Cards can be made interactive with hover states for navigation purposes"
       
      >
          <HStack gap={4} wrap="wrap">
            <Card
              className="w-64 cursor-pointer transition-shadow hover:shadow-md"
              onClick={() => {}}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.preventDefault(); }}
            >
              <CardContent>
                <HStack justify="between" align="center">
                  <div>
                    <Text size="sm" weight="semibold">File a Claim</Text>
                    <Text size="xs" color="muted">Start a new claim</Text>
                  </div>
                  <Icon icon={ArrowRight} size="sm" />
                </HStack>
              </CardContent>
            </Card>
            <Card
              className="w-64 cursor-pointer transition-shadow hover:shadow-md"
              onClick={() => {}}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.preventDefault(); }}
            >
              <CardContent>
                <HStack justify="between" align="center">
                  <div>
                    <Text size="sm" weight="semibold">View Policies</Text>
                    <Text size="xs" color="muted">Manage coverage</Text>
                  </div>
                  <Icon icon={ArrowRight} size="sm" />
                </HStack>
              </CardContent>
            </Card>
          </HStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const CardShowcase: ShowcaseDefinition = {
  id: 'card',
  name: 'Card',
  category: 'layout',
  description: 'Container component for grouping related content',
  propsDefinition: [
    {
      name: 'variant',
      type: 'select',
      options: ['default', 'outlined', 'elevated'],
      defaultValue: 'default',
      description: 'Visual style of the card',
    },
    {
      name: 'padding',
      type: 'select',
      options: ['none', 'sm', 'md', 'lg'],
      defaultValue: 'md',
      description: 'Internal padding of the card content',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'Uses <article> element for semantic grouping',
      'aria-expanded on collapsible headers',
      'role="button" for clickable cards',
    ],
    keyboardNav: [
      'Tab: Navigate to focusable elements',
      'Enter/Space: Toggle collapsible cards',
      'Enter/Space: Activate clickable cards',
    ],
    notes: [
      'Use proper heading hierarchy within CardHeader',
      'Collapsible cards have visible expand/collapse indicators',
      'Clickable cards should have clear hover states',
    ],
  },
  component: CardShowcaseComponent,
};
